import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { useAuth } from "./useAuth"
import { useEffect } from "react"

type AccessRequest = {
  id: string
  requester_id: string
  type: 'visitante' | 'delivery' | 'servico' | 'mudancas' | 'outros'
  person_company: string
  document?: string
  scheduled_date: string
  scheduled_time: string
  observations?: string
  recurrence: 'nenhuma' | 'semanalmente' | 'mensalmente'
  status: 'pending' | 'approved' | 'rejected' | 'completed'
  condominium_id: string
  approved_by?: string
  approved_at?: string
  created_at: string
  updated_at: string
}

type CreateAccessRequestData = {
  type: 'visitante' | 'delivery' | 'servico' | 'mudancas' | 'outros'
  person_company: string
  document?: string
  scheduled_date: string
  scheduled_time: string
  observations?: string
  recurrence: 'nenhuma' | 'semanalmente' | 'mensalmente'
}

export const useAccessRequests = () => {
  const { profile } = useAuth()
  const queryClient = useQueryClient()
  
  // Configurar realtime para atualização automática
  useEffect(() => {
    if (!profile?.condominium_id) return

    console.log('🔄 Configurando realtime para access_requests:', profile.condominium_id)

    const channel = supabase
      .channel('access-requests-changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Escuta INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'access_requests',
          filter: `condominium_id=eq.${profile.condominium_id}`
        },
        (payload) => {
          console.log('🔄 Realtime update received:', payload)
          
          // Invalidar e recarregar dados
          queryClient.invalidateQueries({ queryKey: ["access_requests", profile.condominium_id] })
          queryClient.invalidateQueries({ queryKey: ["my_access_requests"] })
        }
      )
      .subscribe((status) => {
        console.log('📡 Realtime status:', status)
      })

    return () => {
      console.log('🔌 Desconectando realtime')
      supabase.removeChannel(channel)
    }
  }, [profile?.condominium_id, queryClient])
  
  return useQuery({
    queryKey: ["access_requests", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) return []

      const { data, error } = await supabase
        .from("access_requests")
        .select("*")
        .eq("condominium_id", profile.condominium_id)
        .order("created_at", { ascending: false })

      if (error) throw error
      return data as AccessRequest[]
    },
    enabled: !!profile?.condominium_id,
    staleTime: 0, // Sempre buscar dados frescos
    refetchInterval: 5000 // Fallback: refetch a cada 5 segundos
  })
}

export const useMyAccessRequests = () => {
  const { profile } = useAuth()
  const queryClient = useQueryClient()
  
  // Configurar realtime para atualização automática
  useEffect(() => {
    if (!profile?.id) return

    console.log('🔄 Configurando realtime para my_access_requests:', profile.id)

    const channel = supabase
      .channel('my-access-requests-changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Escuta INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'access_requests',
          filter: `requester_id=eq.${profile.id}`
        },
        (payload) => {
          console.log('🔄 Realtime update received for my requests:', payload)
          
          // Invalidar e recarregar dados
          queryClient.invalidateQueries({ queryKey: ["my_access_requests", profile.id] })
          queryClient.invalidateQueries({ queryKey: ["access_requests"] })
        }
      )
      .subscribe((status) => {
        console.log('📡 My requests realtime status:', status)
      })

    return () => {
      console.log('🔌 Desconectando my requests realtime')
      supabase.removeChannel(channel)
    }
  }, [profile?.id, queryClient])
  
  return useQuery({
    queryKey: ["my_access_requests", profile?.id],
    queryFn: async () => {
      if (!profile?.id) {
        console.log('useMyAccessRequests: profile.id não disponível', { profile })
        return []
      }

      console.log('useMyAccessRequests: Buscando para usuário', profile.id)

      const { data, error } = await supabase
        .from("access_requests")
        .select("*")
        .eq("requester_id", profile.id)
        .order("created_at", { ascending: false })

      if (error) {
        console.error('useMyAccessRequests: Erro na query', error)
        throw error
      }
      
      console.log('useMyAccessRequests: Resultados encontradas:', data?.length || 0, data)
      return data as AccessRequest[]
    },
    enabled: !!profile?.id,
    retry: 2,
    staleTime: 0, // Sempre buscar dados frescos
    refetchInterval: 5000 // Fallback: refetch a cada 5 segundos
  })
}

export const useCreateAccessRequest = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async (data: CreateAccessRequestData) => {
      if (!profile?.condominium_id || !profile?.id) {
        throw new Error("Usuário sem condomínio associado")
      }

      const { data: request, error } = await supabase
        .from("access_requests")
        .insert({
          ...data,
          requester_id: profile.id,
          condominium_id: profile.condominium_id
        })
        .select()
        .single()

      if (error) throw error
      return request
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["access_requests"] })
      queryClient.invalidateQueries({ queryKey: ["my_access_requests"] })
      toast.success("Solicitação de acesso criada com sucesso!")
    },
    onError: (error: any) => {
      console.error("Erro ao criar solicitação:", error)
      const message = error?.message || error?.error_description || 'Erro ao criar solicitação de acesso'
      toast.error(message)
    }
  })
}

export const useUpdateAccessRequest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ 
      id, 
      data 
    }: { 
      id: string
      data: Partial<AccessRequest> 
    }) => {
      const { data: request, error } = await supabase
        .from("access_requests")
        .update(data)
        .eq("id", id)
        .select()
        .single()

      if (error) throw error
      return request
    },
    onSuccess: () => {
      // Cache clearing mais agressivo
      queryClient.removeQueries({ queryKey: ["access_requests"] })
      queryClient.removeQueries({ queryKey: ["my_access_requests"] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      
      // Refetch manual após pequeno delay
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: ["access_requests"] })
        queryClient.refetchQueries({ queryKey: ["my_access_requests"] })
      }, 500)
      
      toast.success("Solicitação atualizada com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao atualizar solicitação:", error)
      toast.error("Erro ao atualizar solicitação")
    }
  })
}

export const useDeleteAccessRequest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("access_requests")
        .delete()
        .eq("id", id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["access_requests"] })
      queryClient.invalidateQueries({ queryKey: ["my_access_requests"] })
      toast.success("Solicitação removida com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao remover solicitação:", error)
      toast.error("Erro ao remover solicitação")
    }
  })
}